//@ts-expect-error due to missing support for typescript module declaration
import { Clicksign } from '@/assets/scripts/clicksign-widget-v3.js'
import { useCancelContract, useReadContract } from '@/api/accounting-dealer'
import { clickSignWidgetUrl, env } from '@/env'
import { routes } from '@/router'
import { Box, CircularProgress, Stack, styled, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { generatePath, useNavigate, useOutletContext } from 'react-router-dom'
import { ReadDealResponse } from '@/types'
import { LoadingButton } from '@mui/lab'
import { useQueryClient } from '@tanstack/react-query'

export interface ClicksignWidget {
  endpoint: string
  origin: string
  mount: (t: string) => HTMLIFrameElement
  unmount: () => boolean
  on: (t: string, n: (event: unknown) => void) => void
}

export const StyledClicksignContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '80vh',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  overflow: 'hidden',
  '& iframe': {
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.2)',
  },
}))

export const ContractPage = () => {
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [waitingSignatureWebhook, setWaitingSignatureWebhook] = useState(false)

  const { deal } = useOutletContext<ReadDealResponse>()

  const [widgetIsBeingMounted, setWidgetIsBeingMounted] = useState(true)
  const [widget, setWidget] = useState<ClicksignWidget>()

  const {
    data: contractSignatureResponse,
    isLoading: loadingContractSignature,
    error: contractError,
  } = useReadContract({ deal_key: deal.key })

  const { mutateAsync: cancelContract, isPending: isCancellingContract } =
    useCancelContract()

  const signature_request_key = contractSignatureResponse?.signature_request_key

  useEffect(() => {
    if (!signature_request_key) return

    widget?.unmount()

    setWidget(Clicksign(signature_request_key))
  }, [contractSignatureResponse])

  useEffect(() => {
    setWidgetIsBeingMounted(false)
  }, [contractError])

  useEffect(() => {
    if (!widget) return

    widget.endpoint = clickSignWidgetUrl[env.MODE]

    widget.origin = location.origin

    widget.mount('clicksign-widget-container')

    widget.on('loaded', function () {
      setWidgetIsBeingMounted(false)
    })

    widget.on('signed', function () {
      queryClient.invalidateQueries({
        queryKey: ['deals', { account_key: deal.account_key }],
      })
      localStorage.setItem('selected_company', deal.company.key)
      navigate(routes.accounting.index)
      setWaitingSignatureWebhook(true)
    })
  }, [widget])

  const handleRollback = async () => {
    await cancelContract(deal.key)
    navigate(generatePath(routes.accounting.hiring.mainPartner, { deal_key: deal.key }))
  }

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems="center">
      {widgetIsBeingMounted ||
      loadingContractSignature ||
      waitingSignatureWebhook ? (
        <CircularProgress />
      ) : (
        <StyledClicksignContainer id="clicksign-widget-container" />
      )}
      <Stack flexDirection={'column'} alignItems="center" mt={2}>
        <Typography variant="h6">Há algo de errado com seus dados?</Typography>

        <LoadingButton
          sx={{ mt: 2 }}
          variant="outlined"
          onClick={handleRollback}
          loading={isCancellingContract}
        >
          Voltar e editar dados
        </LoadingButton>
      </Stack>
    </Box>
  )
}
